import React, { useState, useEffect, useRef } from 'react'; // useRef used for geocodeTimer
import { locationService, employeeService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  MapPin,
  Edit,
  Trash2,
  Navigation,
  Building2,
  Users,
  Loader2,
  MapPinned,
  Clock,
} from 'lucide-react';

// Nominatim geocoding (OpenStreetMap) — no API key required
const geocodeAddress = async (query) => {
  if (!query || query.length < 3) return null;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=in`,
      { headers: { 'Accept-Language': 'en' } }
    );
    const data = await res.json();
    if (data.length > 0) return { lat: parseFloat(data[0].lat).toFixed(6), lng: parseFloat(data[0].lon).toFixed(6) };
  } catch (_) {}
  return null;
};

export default function LocationsPage() {
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [latLngTouched, setLatLngTouched] = useState(false);
  const geocodeTimer = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    latitude: '',
    longitude: '',
    radius_meters: 100,
    working_hours_start: '09:00',
    working_hours_end: '18:00',
    is_enabled: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (latLngTouched) return;
    const addressQuery = [formData.address, formData.city, formData.state, formData.country]
      .filter(Boolean).join(', ').trim();
    if (addressQuery.length < 5) return;
    clearTimeout(geocodeTimer.current);
    geocodeTimer.current = setTimeout(async () => {
      const result = await geocodeAddress(addressQuery);
      if (result) {
        setFormData(prev => ({ ...prev, latitude: result.lat, longitude: result.lng }));
      }
    }, 700);
    return () => clearTimeout(geocodeTimer.current);
  }, [formData.address, formData.city, formData.state, formData.country, latLngTouched]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [locs, emps] = await Promise.all([
        locationService.list(),
        employeeService.list(),
      ]);
      setLocations(locs);
      setEmployees(emps);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatLngTouched(true);
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
        }));
        toast.success('Location captured!');
      },
      (error) => {
        toast.error('Failed to get location: ' + error.message);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleAddLocation = async () => {
    if (!formData.name || !formData.city || !formData.latitude || !formData.longitude) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddLoading(true);
    try {
      await locationService.create({
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius_meters: parseFloat(formData.radius_meters),
      });
      toast.success('Location added successfully!');
      setShowAddDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add location');
    } finally {
      setAddLoading(false);
    }
  };

  const handleToggleLocation = async (locationId, isEnabled) => {
    try {
      await locationService.update(locationId, { is_enabled: !isEnabled });
      toast.success(`Location ${!isEnabled ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update location');
    }
  };

  const handleOpenEdit = (location) => {
    setSelectedLocation(location);
    setFormData({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      country: location.country || 'India',
      latitude: location.latitude?.toString() || '',
      longitude: location.longitude?.toString() || '',
      radius_meters: location.radius_meters || 100,
      working_hours_start: location.working_hours_start || '09:00',
      working_hours_end: location.working_hours_end || '18:00',
      is_enabled: location.is_enabled ?? true,
    });
    setLatLngTouched(false);
    setShowEditDialog(true);
  };

  const handleUpdateLocation = async () => {
    if (!selectedLocation) return;
    if (!formData.name || !formData.city || !formData.latitude || !formData.longitude) {
      toast.error('Please fill in all required fields');
      return;
    }

    setEditLoading(true);
    try {
      await locationService.update(selectedLocation.id, {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius_meters: parseFloat(formData.radius_meters),
      });
      toast.success('Location updated successfully!');
      setShowEditDialog(false);
      setSelectedLocation(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    } finally {
      setEditLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      latitude: '',
      longitude: '',
      radius_meters: 100,
      working_hours_start: '09:00',
      working_hours_end: '18:00',
      is_enabled: true,
    });
    setLatLngTouched(false);
  };

  const getAssignedEmployeesCount = (locationId) => {
    return employees.filter(e => e.location_ids?.includes(locationId)).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Work Locations</h1>
          <p className="text-muted-foreground mt-1">
            Manage office locations and geofence settings
          </p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="glow-primary" data-testid="add-location-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Location</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Location Name / Search *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Search on maps or enter name"
                    data-testid="location-name-input"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Business Street"
                    data-testid="address-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Mumbai"
                    data-testid="city-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="Maharashtra"
                    data-testid="state-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Latitude *</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={getCurrentLocation}
                    >
                      <Navigation className="w-4 h-4 mr-1" />
                      Get Current
                    </Button>
                  </div>
                  <Input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => {
                      setLatLngTouched(true);
                      setFormData({ ...formData, latitude: e.target.value });
                    }}
                    placeholder="19.0760"
                    data-testid="latitude-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Longitude *</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => {
                      setLatLngTouched(true);
                      setFormData({ ...formData, longitude: e.target.value });
                    }}
                    placeholder="72.8777"
                    data-testid="longitude-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Geofence Radius (meters)</Label>
                  <Input
                    type="number"
                    value={formData.radius_meters}
                    onChange={(e) => setFormData({ ...formData, radius_meters: e.target.value })}
                    placeholder="100"
                    data-testid="radius-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Working Hours</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={formData.working_hours_start}
                      onChange={(e) => setFormData({ ...formData, working_hours_start: e.target.value })}
                      className="flex-1"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="time"
                      value={formData.working_hours_end}
                      onChange={(e) => setFormData({ ...formData, working_hours_end: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Map Preview */}
              {formData.latitude && formData.longitude && (
                <div className="space-y-2">
                  <Label>Map Preview</Label>
                  <div className="rounded-lg overflow-hidden border border-border h-48">
                    <iframe
                      title="Location Map Preview"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(formData.longitude)-0.005},${parseFloat(formData.latitude)-0.005},${parseFloat(formData.longitude)+0.005},${parseFloat(formData.latitude)+0.005}&layer=mapnik&marker=${formData.latitude},${formData.longitude}`}
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddLocation} disabled={addLoading} data-testid="submit-location-btn">
                {addLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Location
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Location</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Location Name / Search *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Search on maps or enter name"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Business Street"
                  />
                </div>

                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Mumbai"
                  />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="Maharashtra"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Latitude *</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={getCurrentLocation}
                    >
                      <Navigation className="w-4 h-4 mr-1" />
                      Get Current
                    </Button>
                  </div>
                  <Input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => {
                      setLatLngTouched(true);
                      setFormData({ ...formData, latitude: e.target.value });
                    }}
                    placeholder="19.0760"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Longitude *</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => {
                      setLatLngTouched(true);
                      setFormData({ ...formData, longitude: e.target.value });
                    }}
                    placeholder="72.8777"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Geofence Radius (meters)</Label>
                  <Input
                    type="number"
                    value={formData.radius_meters}
                    onChange={(e) => setFormData({ ...formData, radius_meters: e.target.value })}
                    placeholder="100"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Working Hours</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={formData.working_hours_start}
                      onChange={(e) => setFormData({ ...formData, working_hours_start: e.target.value })}
                      className="flex-1"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="time"
                      value={formData.working_hours_end}
                      onChange={(e) => setFormData({ ...formData, working_hours_end: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {formData.latitude && formData.longitude && (
                <div className="space-y-2">
                  <Label>Map Preview</Label>
                  <div className="rounded-lg overflow-hidden border border-border h-48">
                    <iframe
                      title="Location Map Preview"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(formData.longitude)-0.005},${parseFloat(formData.latitude)-0.005},${parseFloat(formData.longitude)+0.005},${parseFloat(formData.latitude)+0.005}&layer=mapnik&marker=${formData.latitude},${formData.longitude}`}
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateLocation} disabled={editLoading}>
                {editLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Locations</p>
                <p className="text-2xl font-bold">{locations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10 text-green-500">
                <MapPinned className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{locations.filter(l => l.is_enabled).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/10 text-purple-500">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Employees Assigned</p>
                <p className="text-2xl font-bold">
                  {employees.filter(e => e.location_ids?.length > 0).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="locations-grid">
        {locations.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MapPin className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No locations added yet</p>
              <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Location
              </Button>
            </CardContent>
          </Card>
        ) : (
          locations.map((location) => (
            <Card key={location.id} className="card-hover overflow-hidden">
              {/* Map Preview */}
              {location.latitude && location.longitude && (
                <div className="h-32 bg-secondary">
                  <iframe
                    title={`Map for ${location.name}`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${location.longitude-0.005},${location.latitude-0.005},${location.longitude+0.005},${location.latitude+0.005}&layer=mapnik&marker=${location.latitude},${location.longitude}`}
                  />
                </div>
              )}
              
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className={`w-4 h-4 ${location.is_enabled ? 'text-green-500' : 'text-gray-500'}`} />
                      {location.name}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {location.address || location.city}
                    </CardDescription>
                  </div>
                  <Switch
                    checked={location.is_enabled}
                    onCheckedChange={() => handleToggleLocation(location.id, location.is_enabled)}
                    data-testid={`toggle-${location.id}`}
                  />
                </div>
              </CardHeader>
              
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Navigation className="w-4 h-4 text-muted-foreground" />
                  <span className="font-mono text-xs">
                    {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <div className="geofence-pulse w-3 h-3 rounded-full bg-primary" />
                  <span>Radius: {location.radius_meters}m</span>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>{location.working_hours_start} - {location.working_hours_end}</span>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span>{getAssignedEmployeesCount(location.id)} employees assigned</span>
                </div>

                <div className="pt-3 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleOpenEdit(location)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button variant="outline" size="sm">
                    <Users className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
